import { useCallback, useLayoutEffect, useRef, type ReactNode } from 'react';
import { useIntersectionObserver } from '@/core/hooks/useIntersectionObserver';
import type { ListingDesignItem } from '../../domain/models/ProductListing';

const VIEW_CONFIRM_DELAY_MS = 2000;

function isElementFullyVisible(element: Element): boolean {
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= windowHeight &&
    rect.right <= windowWidth
  );
}

export interface PlpProductViewTrackerProps {
  product: ListingDesignItem;
  scrollDepth: number;
  horizontalPosition: number;
  viewedDesignIds: Set<number>;
  enabled?: boolean;
  fullyVisible?: boolean;
  onProductViewed: (params: {
    product: ListingDesignItem;
    scrollDepth: number;
    horizontalPosition: number;
  }) => void;
  children: ReactNode;
}

/**
 * Fires `listing_page_product_viewed` once per product when it stays visible for 2s.
 * Mirrors old-react VisibilityObserver + handleProductItemVisibility behaviour.
 */
export function PlpProductViewTracker({
  product,
  scrollDepth,
  horizontalPosition,
  viewedDesignIds,
  enabled = true,
  fullyVisible = true,
  onProductViewed,
  children,
}: PlpProductViewTrackerProps) {
  const designId = product.designId;
  const onProductViewedRef = useRef(onProductViewed);
  const scrollDepthRef = useRef(scrollDepth);
  const horizontalPositionRef = useRef(horizontalPosition);
  const productRef = useRef(product);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    onProductViewedRef.current = onProductViewed;
    scrollDepthRef.current = scrollDepth;
    horizontalPositionRef.current = horizontalPosition;
    productRef.current = product;
  }, [onProductViewed, scrollDepth, horizontalPosition, product]);

  const clearPendingTimer = useCallback(() => {
    if (pendingTimerRef.current != null) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }
  }, []);

  const onIntersect = useCallback(
    (entry: IntersectionObserverEntry) => {
      if (!enabled) return;
      if (viewedDesignIds.has(designId)) return;

      const isVisibleNow = fullyVisible
        ? entry.isIntersecting && entry.intersectionRatio >= 1
        : entry.isIntersecting;

      if (!isVisibleNow) {
        clearPendingTimer();
        return;
      }

      // Keep the existing dwell timer while the card stays visible.
      // Re-starting on every IO callback (virtualizer layout, scroll jitter) never
      // let the 2s delay complete.
      if (pendingTimerRef.current != null) {
        return;
      }

      pendingTimerRef.current = setTimeout(() => {
        pendingTimerRef.current = null;
        if (viewedDesignIds.has(designId)) return;

        const domElement = wrapperRef.current;
        if (!domElement || !domElement.isConnected) return;
        if (fullyVisible && !isElementFullyVisible(domElement)) return;

        viewedDesignIds.add(designId);
        onProductViewedRef.current({
          product: productRef.current,
          scrollDepth: scrollDepthRef.current,
          horizontalPosition: horizontalPositionRef.current,
        });
      }, VIEW_CONFIRM_DELAY_MS);
    },
    [clearPendingTimer, designId, enabled, viewedDesignIds, fullyVisible],
  );

  useLayoutEffect(() => () => clearPendingTimer(), [clearPendingTimer]);

  const { ref: observerRef } = useIntersectionObserver<HTMLDivElement>(
    { fullyVisible, enabled },
    onIntersect,
  );

  const setWrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      wrapperRef.current = node;
      observerRef.current = node;
    },
    [observerRef],
  );

  return (
    <div ref={setWrapperRef} className="min-w-0">
      {children}
    </div>
  );
}
